import queryString from "query-string";
import { PureComponent } from "react";

import * as CalculatorStepTracker from "@/services/tracking/trackers/tools/calculators/calculatorStep.tracker";
import { CALCULATOR_KEYS } from "@/services/tracking/trackers/tools/calculators/config";
import { asyncState, validNumber } from "@/utils/calculators";

import Calculation from "./calculation/calculation.component";
import Result from "./result/result.component";

interface BrickCalculatorState {
    wallLength: number;
    wallHeight: number;
    brickWidth: number;
    brickLength: number;
    brickHeight: number;
    wallArea: number;
    brickArea: number;
    numberOfBricksHalf: number;
    numberOfBricksFull: number;
    mortarVolumeHalf: number;
    mortarVolumeFull: number;
    jointCementVolume: number;
}

class BrickCalculator extends PureComponent<{}, BrickCalculatorState> {
    query = queryString.parse(location.search);

    constructor(props: {}) {
        super(props);

        this.state = {
            wallLength: this.query.wallLength
                ? Number(this.query.wallLength)
                : 0,
            wallHeight: this.query.wallHeight
                ? Number(this.query.wallHeight)
                : 0,
            brickWidth: this.query.brickWidth
                ? Number(this.query.brickWidth)
                : 0,
            brickLength: this.query.brickLength
                ? Number(this.query.brickLength)
                : 0,
            brickHeight: this.query.brickHeight
                ? Number(this.query.brickHeight)
                : 0,
            wallArea: 0,
            brickArea: 0,
            numberOfBricksHalf: 0,
            numberOfBricksFull: 0,
            mortarVolumeHalf: 0,
            mortarVolumeFull: 0,
            jointCementVolume: 0
        };
    }

    async componentDidMount() {
        await this.calculateFields(true);
    }

    // calculations
    wallArea = () => {
        let wallArea = 0;
        const wallLength = this.state.wallLength;
        const wallHeight = this.state.wallHeight;

        if (validNumber(wallLength) && validNumber(wallHeight)) {
            wallArea = wallLength * wallHeight;
        }

        return wallArea;
    };
    brickArea = () => {
        let brickArea = 0;

        const brickLength = this.state.brickLength;

        const brickHeight = this.state.brickHeight;

        if (validNumber(brickLength) && validNumber(brickHeight)) {
            brickArea = (brickLength + 1) * (brickHeight + 1) * 0.0001;
        }

        return brickArea;
    };
    numberOfBricks = (adjustmentFactor: number) => {
        let numberOfBricks = 0;

        const wallArea = this.state.wallArea;

        const brickArea = this.state.brickArea;

        if (validNumber(wallArea) && validNumber(brickArea)) {
            numberOfBricks = (wallArea / brickArea) * adjustmentFactor;
        }

        return numberOfBricks;
    };
    mortarVolumeHalf = () => {
        let mortarVolumeHalf = 0;

        const brickLength = this.state.brickLength;

        const brickWidth = this.state.brickWidth;

        const brickHeight = this.state.brickHeight;

        const brickVolume =
            brickLength * brickWidth * brickHeight * 0.001 * 0.001;

        const volumeTotalBricks = brickVolume * this.state.numberOfBricksHalf;

        const wallVolume = this.state.wallArea * brickWidth * 0.01;

        if (validNumber(wallVolume) && validNumber(volumeTotalBricks)) {
            mortarVolumeHalf = wallVolume - volumeTotalBricks;
        }

        return mortarVolumeHalf;
    };
    mortarVolumeFull = () => {
        let mortarVolumeFull = 0;
        const mortarInBetweenDoubleWall = this.wallArea() * 0.01; // wall area x 1 centimeter
        const mortarVolumeHalf = this.mortarVolumeHalf();

        if (
            validNumber(mortarVolumeHalf) &&
            validNumber(mortarInBetweenDoubleWall)
        ) {
            mortarVolumeFull = mortarVolumeHalf * 2 + mortarInBetweenDoubleWall;
        }

        return mortarVolumeFull;
    };
    joinCementVolume = () => {
        const areaJointCement = 0.01 * 0.01;

        const numberOfBricks = this.state.numberOfBricksHalf;

        const brickHeight = this.state.brickHeight / 100;

        const brickLength = this.state.brickLength / 100;
        const brickDeadSpace = areaJointCement * 0.01;

        return (
            brickLength * numberOfBricks * areaJointCement +
            brickHeight * numberOfBricks * areaJointCement +
            brickDeadSpace * numberOfBricks
        );
    };

    // setters
    setBrickMeasurements = (length: number, width: number, height: number) => {
        this.setState(
            {
                brickLength: length,
                brickWidth: width,
                brickHeight: height
            },
            () => {
                this.calculateFields();
            }
        );
    };
    setCalculationValue = (field: string, value: string) => {
        const numberValue =
            value === "" ? 0 : parseFloat(value.replace(",", "."));
        this.setState(
            (state) => ({ ...state, [field]: numberValue }),
            async () => {
                await this.calculateFields();
            }
        );
    };
    calculateFields = async (initialCall: boolean = false) => {
        await asyncState(this, {
            wallArea: this.wallArea(),
            brickArea: this.brickArea()
        });
        await asyncState(this, {
            numberOfBricksHalf: Math.ceil(this.numberOfBricks(1.02)),
            numberOfBricksFull: Math.ceil(this.numberOfBricks(2.04))
        });
        await asyncState(this, {
            mortarVolumeHalf: this.mortarVolumeHalf(),
            mortarVolumeFull: this.mortarVolumeFull(),
            jointCementVolume: this.joinCementVolume()
        });

        if (initialCall) {
            CalculatorStepTracker.trackStart(CALCULATOR_KEYS.brick);
        } else {
            CalculatorStepTracker.trackSubmit(CALCULATOR_KEYS.brick);
        }
    };

    render() {
        const calculationProps = {
            setCalculationValue: this.setCalculationValue,
            setBrickMeasurements: this.setBrickMeasurements,
            wallLength: this.state.wallLength,
            wallHeight: this.state.wallHeight,
            brickLength: this.state.brickLength,
            brickWidth: this.state.brickWidth,
            brickHeight: this.state.brickHeight
        };
        const resultsProps = {
            brickWidth: this.state.brickWidth,
            brickLength: this.state.brickLength,
            wallArea: this.state.wallArea,
            numberOfBricksHalf: this.state.numberOfBricksHalf,
            numberOfBricksFull: this.state.numberOfBricksFull,
            mortarVolumeHalf: this.state.mortarVolumeHalf,
            mortarVolumeFull: this.state.mortarVolumeFull,
            jointCementVolume: this.state.jointCementVolume,
            mailParams: {
                wallLength: this.state.wallLength,
                wallHeight: this.state.wallHeight,
                brickLength: this.state.brickLength,
                brickWidth: this.state.brickWidth,
                brickHeight: this.state.brickHeight
            }
        };

        return (
            <div id="calculator">
                <Calculation {...calculationProps} />
                <Result {...resultsProps} />
            </div>
        );
    }
}

export { BrickCalculator };
